<template>
  <JoszakiModal
    :open="open"
    size="lg"
    :title="$t('conductor.zones.mapSelector.modalTitle')"
    :disable-confirm="!pickedZones.length && !zonesToRemove.length"
    :confirm-button-label="confirmButtonLabel"
    @close="onCancel"
    @cancel="onCancel"
    @confirm="onConfirm"
  >
    <template #body>
      <div v-if="personMiniMarkets.length" class="mb-1">
        <div class="text-sm font-bold mb-1">
          {{ $t("conductor.zones.mapSelector.personMiniMarketsLabel") }}
        </div>
        <div class="flex flex-wrap gap-1">
          <JoszakiPill
            v-for="mm in personMiniMarkets"
            :key="`person-mm-${mm.id}`"
            :type="isSelectedMiniMarket(mm) ? colorType : 'default'"
            hoverable
            class="cursor-pointer text-xs"
            @click.native="selectMiniMarketById(mm.id)"
          >
            {{ mm.name }}
          </JoszakiPill>
        </div>
      </div>

      <div class="grid md:grid-cols-2 gap-2">
        <AutocompleteMiniMarket
          v-model="selectedMiniMarket"
          :label="$t('conductor.zones.mapSelector.miniMarketLabel')"
          @select="onMiniMarketSelect"
        />
        <JoszakiAutocomplete
          v-model="selectedVersion"
          :items="versions"
          :disabled="!selectedMiniMarket"
          :label="$t('conductor.zones.mapSelector.versionLabel')"
          value-field="note"
        />
      </div>

      <div v-if="existingZones.length">
        <div class="text-sm font-bold mb-1">
          {{ $t("conductor.zones.mapSelector.currentZonesLabel", { entity: entityName }) }}
        </div>
        <div
          v-if="!currentZones.length"
          class="text-sm italic text-gray-500 dark:text-gray-400"
        >
          {{ $t("conductor.zones.mapSelector.emptyCurrentZones") }}
        </div>
        <div v-else class="flex flex-wrap gap-1">
          <JoszakiPill
            v-for="zone in currentZones"
            :key="`current-${zone.id}`"
            :type="colorType"
            hoverable
            class="cursor-pointer text-xs"
            @click.native="toggleRemove(zone)"
          >
            {{ zone.fullName }}
            <IconComponent icon="times-circle" class="inline" />
          </JoszakiPill>
        </div>
      </div>

      <div>
        <div class="text-sm font-bold mb-1">
          {{ $t("conductor.zones.mapSelector.pickedZonesLabel", { entity: entityName }) }}
        </div>
        <div
          v-if="!pickedZones.length"
          class="text-sm italic text-gray-500 dark:text-gray-400"
        >
          {{ $t("conductor.zones.mapSelector.emptyPickedZones") }}
        </div>
        <div v-else class="flex flex-wrap gap-1">
          <JoszakiPill
            v-for="zone in pickedZones"
            :key="`picked-${zone.id}`"
            :type="colorType"
            inverted
            hoverable
            class="cursor-pointer text-xs"
            @click.native="togglePick(zone)"
          >
            {{ zone.fullName }}
            <IconComponent icon="times-circle" class="inline" />
          </JoszakiPill>
        </div>
      </div>

      <div v-if="zonesToRemove.length">
        <div class="text-sm font-bold mb-1">
          {{ $t("conductor.zones.mapSelector.zonesToRemoveLabel", { entity: entityName }) }}
        </div>
        <div class="flex flex-wrap gap-1">
          <JoszakiPill
            v-for="zone in zonesToRemove"
            :key="`remove-${zone.id}`"
            type="danger"
            hoverable
            class="cursor-pointer text-xs"
            @click.native="toggleRemove(zone)"
          >
            {{ zone.fullName }}
            <IconComponent icon="undo" class="inline" />
          </JoszakiPill>
        </div>
      </div>

      <JoszakiLoading :active="isLoading" />
      <ZoneSelectorMap
        :zones="visibleZones"
        :selected-zone-ids="pickedZoneIds"
        :existing-zone-ids="existingZoneIds"
        :removed-zone-ids="removedZoneIds"
        :reference-zone-ids="referenceZoneIds"
        :color-type="colorType"
        :reference-color-type="referenceColorType"
        :reference-selectable="referenceSelectable"
        :reference-hint-key="referenceHintKey"
        :person-city="personCity"
        @toggle="togglePick"
      />
    </template>
  </JoszakiModal>
</template>

<script>
import { computed, ref, watch, useContext } from "@nuxtjs/composition-api";
import AutocompleteMiniMarket from "~/components/_refactored/autocomplete/AutocompleteMiniMarket.vue";
import ZoneSelectorMap from "~/components/_refactored/autocomplete/ZoneSelectorMap.vue";
import { useZoneActions } from "~/components/_refactored/conductor/zones/useZoneActions";
import { useMiniMarketActions } from "~/components/_refactored/conductor/mini-markets/useMiniMarketActions";
import { useDataTableFilterOperators } from "~/components/_refactored/conductor/data-table/useDataTableFilterOperators";
import { useGetLatLngForZone } from "~/composables/zone/useGetLatLngForZone";
import { useZoneStore } from "~/stores/zone";

export default {
  components: {
    AutocompleteMiniMarket,
    ZoneSelectorMap,
  },
  props: {
    open: {
      type: Boolean,
      required: true,
    },
    existingZones: {
      type: Array,
      default: () => [],
    },
    // pill/zone color type (e.g. "success", "rating"), forwarded to the map and
    // pills so the modal matches the autocomplete field it was opened from
    colorType: {
      type: String,
      default: "success",
    },
    // zones from the other list shown for context (e.g. the person's active
    // zones on the waiting-list map, or waiting-list zones on the active map)
    referenceZones: {
      type: Array,
      default: () => [],
    },
    // color of the reference zones — matches the field they belong to
    referenceColorType: {
      type: String,
      default: "success",
    },
    // whether reference zones can be picked (e.g. promote a waiting-list zone to
    // active on the active-zones map); false makes them read-only
    referenceSelectable: {
      type: Boolean,
      default: false,
    },
    // i18n key for the reference zone tooltip hint
    referenceHintKey: {
      type: String,
      default: "conductor.zones.mapSelector.referenceHint",
    },
    // lowercase plural noun for the zones in this context (e.g. "zónák" or
    // "várólista-jelentkezések"), used in the current/adding/removing labels
    entityLabel: {
      type: String,
      default: "",
    },
    // the edited person's city, forwarded to the map as a pin
    personCity: {
      type: Object,
      default: null,
    },
  },
  setup(props, { emit }) {
    const { i18n } = useContext();
    const { getZones } = useZoneActions();
    const { getMiniMarketById } = useMiniMarketActions();
    const OPERATORS = useDataTableFilterOperators();
    const { getLatLngForZone } = useGetLatLngForZone();
    // only used to load subcounty geometries for the zone polygons (same as ZoneEditForm)
    const zoneStore = useZoneStore();

    const selectedMiniMarket = ref(null);
    const selectedVersion = ref(null);
    const pickedZones = ref([]);
    const zonesToRemove = ref([]);
    const zones = ref([]);
    const isLoading = ref(false);

    const reset = () => {
      selectedMiniMarket.value = null;
      selectedVersion.value = null;
      pickedZones.value = [];
      zonesToRemove.value = [];
      zones.value = [];
    };

    // unique mini markets the person already has zones in (existing + reference),
    // surfaced as quick-select chips at the top of the dialog
    const personMiniMarkets = computed(() => {
      const byId = new Map();
      for (const zone of [...props.existingZones, ...props.referenceZones]) {
        const miniMarket = zone.miniMarket;
        if (miniMarket?.id != null && !byId.has(miniMarket.id)) {
          byId.set(miniMarket.id, miniMarket);
        }
      }
      return [...byId.values()];
    });

    const isSelectedMiniMarket = (miniMarket) =>
      selectedMiniMarket.value?.id === miniMarket.id;

    // the zones only carry { id, name } for their mini market, so fetch the full
    // record (with versions) before selecting it to drive the zone/version load
    const selectMiniMarketById = async (id) => {
      try {
        const miniMarket = await getMiniMarketById(id);
        // the dialog may have been closed/reopened while the fetch was in
        // flight — ignore the stale result rather than clobbering the reset
        if (!props.open) {
          return;
        }
        onMiniMarketSelect(miniMarket);
      } catch (e) {
        // getMiniMarketById already logs/handles the error
      }
    };

    watch(
      () => props.open,
      (isOpen) => {
        if (isOpen) {
          reset();
          const firstMiniMarket = personMiniMarkets.value[0];
          if (firstMiniMarket) {
            selectMiniMarketById(firstMiniMarket.id);
          }
        }
      }
    );

    const versions = computed(
      () => selectedMiniMarket.value?.versions ?? []
    );

    const pickedZoneIds = computed(() =>
      pickedZones.value.map((z) => z.id)
    );

    const existingZoneIds = computed(() =>
      props.existingZones.map((z) => z.id)
    );

    const referenceZoneIds = computed(() =>
      props.referenceZones.map((z) => z.id)
    );

    // falls back to "zones" when the opening field doesn't provide an entity
    const entityName = computed(
      () =>
        props.entityLabel ||
        i18n.t("conductor.zones.mapSelector.entity.zones")
    );

    const removedZoneIds = computed(() =>
      zonesToRemove.value.map((z) => z.id)
    );

    const currentZones = computed(() =>
      props.existingZones.filter(
        (z) => !removedZoneIds.value.includes(z.id)
      )
    );

    const confirmButtonLabel = computed(() => {
      const addCount = pickedZones.value.length;
      const removeCount = zonesToRemove.value.length;
      if (addCount && removeCount) {
        return i18n.t(
          "conductor.zones.mapSelector.confirmButtonAddRemove",
          { addCount, removeCount }
        );
      }
      if (removeCount) {
        return i18n.t("conductor.zones.mapSelector.confirmButtonRemove", {
          count: removeCount,
        });
      }
      return i18n.t("conductor.zones.mapSelector.confirmButtonAdd", {
        count: addCount,
      });
    });

    const visibleZones = computed(() =>
      zones.value.filter((z) => z.latLng)
    );

    const pickDefaultVersion = (miniMarket) => {
      if (!miniMarket?.versions?.length) {
        selectedVersion.value = null;
        return;
      }
      const today = new Date();
      const current = miniMarket.versions.find((v) => {
        const from = v.validFrom ? new Date(v.validFrom) : null;
        const until = v.validUntil ? new Date(v.validUntil) : null;
        return (!from || from <= today) && (!until || until >= today);
      });
      selectedVersion.value = current ?? miniMarket.versions[0];
    };

    const onMiniMarketSelect = (miniMarket) => {
      selectedMiniMarket.value = miniMarket;
      pickDefaultVersion(miniMarket);
    };

    watch(
      [selectedMiniMarket, selectedVersion],
      async ([miniMarket, version]) => {
        if (!miniMarket || !version) {
          zones.value = [];
          return;
        }

        isLoading.value = true;
        try {
          const [zonesResult] = await Promise.all([
            getZones({
              page: 1,
              pageSize: 1000,
              filters: [
                {
                  filterId: "miniMarketId",
                  operator: OPERATORS.EQUALS,
                  value: miniMarket.id,
                },
                {
                  filterId: "miniMarketZoneVersionId",
                  operator: OPERATORS.EQUALS,
                  value: version.id,
                },
              ],
            }),
            zoneStore.loadSubcounties(),
          ]);

          zones.value = zonesResult.items.map((zone) => ({
            ...zone,
            latLng: getLatLngForZone(zone),
          }));
        } finally {
          isLoading.value = false;
        }
      }
    );

    const toggleRemove = (zone) => {
      if (removedZoneIds.value.includes(zone.id)) {
        zonesToRemove.value = zonesToRemove.value.filter(
          (z) => z.id !== zone.id
        );
      } else {
        // prefer the stored zone object so the pill shows the saved name
        const existing =
          props.existingZones.find((z) => z.id === zone.id) ?? zone;
        zonesToRemove.value = [...zonesToRemove.value, existing];
      }
    };

    const togglePick = (zone) => {
      // read-only reference zones (e.g. active zones on the waiting-list map)
      // can't be picked; selectable ones fall through to the normal pick logic
      if (
        !props.referenceSelectable &&
        referenceZoneIds.value.includes(zone.id) &&
        !existingZoneIds.value.includes(zone.id)
      ) {
        return;
      }
      if (existingZoneIds.value.includes(zone.id)) {
        toggleRemove(zone);
        return;
      }
      const existingIndex = pickedZones.value.findIndex(
        (z) => z.id === zone.id
      );
      if (existingIndex >= 0) {
        pickedZones.value = pickedZones.value.filter(
          (_, i) => i !== existingIndex
        );
      } else {
        pickedZones.value = [...pickedZones.value, zone];
      }
    };

    const onConfirm = () => {
      emit("confirm", {
        addedZones: [...pickedZones.value],
        removedZones: [...zonesToRemove.value],
      });
      emit("close");
    };

    const onCancel = () => {
      emit("close");
    };

    return {
      selectedMiniMarket,
      selectedVersion,
      versions,
      pickedZones,
      pickedZoneIds,
      zonesToRemove,
      existingZoneIds,
      referenceZoneIds,
      entityName,
      removedZoneIds,
      currentZones,
      personMiniMarkets,
      selectMiniMarketById,
      isSelectedMiniMarket,
      confirmButtonLabel,
      visibleZones,
      isLoading,
      onMiniMarketSelect,
      togglePick,
      toggleRemove,
      onConfirm,
      onCancel,
    };
  },
};
</script>
